import store from '../../store'
import http, { httpV2 } from './http'
import lo from 'lodash'
import fixUrlSuffix from '../../shared/utils/fixUrlSuffix'
import { API_KEY, API_V2_PREDEFINED_TOKEN } from '../../fixtures/apiUrls'
import axios from 'axios'
import { CANCEL } from 'redux-saga'

// authScheme: the `Authorization` header scheme to use when isProtected is true.
// v1 uses a custom "jwt" scheme; v2 declares HTTPBearer/OAuth2 security, so it needs "Bearer".
// getToken: (auth) => token — defaults to the real session token; v2 overrides this
// below while the backend's v1-token adapter isn't ready yet.
// fixUrl: (url, urlParams) => url — v1 always wants a trailing "/". v2 registers
// list/collection endpoints WITH a trailing slash (e.g. "/lead/lead/?scheme=...")
// but detail/action endpoints WITHOUT one (e.g. "/lead/lead/{uid}", ".../close") —
// see fixUrlSuffixV2 below for how that's detected.
const buildRequest = (httpInstance, authScheme, getToken = (auth) => auth.token, fixUrl = (url) => fixUrlSuffix(url)) => (config, isProtected) => {
  const { auth } = store.getState()

  // 4 Oct 2021. We now use API Key
  config.headers = {
    ...config.headers,
    "X-API-Key": API_KEY,
    "X-Service-Key": auth.userInfo.business_uid
  }

  if (isProtected)
    config.headers = { ...config.headers, Authorization: `${authScheme} ${getToken(auth)}` }

  let url = fixUrl(config.url, config.urlParams)
  if (config.urlParams) {
    const urlStr = Object.keys(config.urlParams)
      .filter(k => !(config.urlParams[k] === null || config.urlParams[k] === undefined))
      .map(k => (
        encodeURIComponent(k) + "=" + encodeURIComponent(config.urlParams[k])
      )).join("&")
    if (urlStr) {
      if (url.indexOf("?") >= 0)
        url += "&" + urlStr
      else
        url += "?" + urlStr
    }
  }

  config = { ...config, url }

  return httpInstance.request(lo.pick(config, ['url', 'method', 'data', 'headers', 'cancelToken', 'timeout', 'onUploadProgress', 'responseType']))
}

const buildMethods = (request) => ({
  get: async (url, opts = {}, isProtected = true) => {
    const options = {
      method: 'get',
      url,
      // urlParams: opts.urlParams,
      // headers: opts.headers,
      ...opts
    }

    return request(options, isProtected)
  },

  post: async (url, body, opts = {}, isProtected = true) => {
    const source = axios.CancelToken.source()
    const options = {
      method: 'post',
      url,
      // urlParams: opts.urlParams,
      data: body,
      // headers: opts.headers,
      cancelToken: source.token,
      ...opts
    }

    request[CANCEL] = () => source.cancel()

    return request(options, isProtected)
  },

  patch: async (url, body, opts = {}, isProtected = true) => {
    const options = {
      method: 'patch',
      url,
      // urlParams: opts.urlParams,
      data: body,
      // headers: opts.headers,
      ...opts
    }

    return request(options, isProtected)
  },

  put: async (url, body, opts = {}, isProtected = true) => {
    const options = {
      method: 'put',
      url,
      // urlParams: opts.urlParams,
      data: body,
      // headers: opts.headers,
      ...opts,
    }

    return request(options, isProtected)
  },

  deleted: async (url, body, opts = {}, isProtected = true) => {
    const options = {
      method: 'delete',
      url,
      // urlParams: opts.urlParams,
      data: body,
      // headers: opts.headers,
      ...opts,
    }

    return request(options, isProtected)
  },
})

export const request = buildRequest(http, 'jwt')
export const { get, post, patch, put, deleted } = buildMethods(request)

// v2 (Core - Staff) API — used by modules calling the new v2 API (e.g. Leads).
//
// TEMPORARY WORKAROUND: backend's "accept a v1 session token in v2" adapter isn't
// live yet, so a real v1 auth.token gets rejected by v2. Until it lands, flip
// use_predefined_token on to send a manually-issued v2 token (REACT_APP_GOAPP_V2_PREDEFINED_TOKEN)
// instead. Set back to false once the backend adapter ships.
const use_predefined_token = false

// v2 routes need EXACTLY the trailing slash the backend registered — bare collection
// paths (list/create) have one, anything with something appended after the resource
// name (an id, or a static sub-path like "groups"/"close"/"option") doesn't. That's not
// derivable from whether the call has query params (e.g. GET .../lead/groups takes
// filter params but has no trailing slash), so each api/*.js call spells out the
// correct URL itself — this stays a no-op rather than guessing.
export const requestV2 = buildRequest(httpV2, 'Bearer', (auth) => use_predefined_token ? API_V2_PREDEFINED_TOKEN : auth.token, (url) => url)
export const { get: getV2, post: postV2, patch: patchV2, put: putV2, deleted: deletedV2 } = buildMethods(requestV2)